import React, { useMemo } from 'react';
import styles from '../styles/components/ActivityHeatmap.module.css';

interface ActivityHeatmapProps {
  data: Record<string, number>;
}

// Helper to format date as YYYY/MM/DD
const formatDateKey = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}/${m}/${d}`;
};

// Generate range of dates for the last year
const generateYearDates = () => {
  const dates: Date[] = [];
  const today = new Date();
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  
  // Align start to the previous Sunday so the grid starts cleanly
  // 0 = Sunday, 1 = Monday, ...
  const dayOfWeek = oneYearAgo.getDay(); 
  // Subtract days to get to previous Sunday (if not already Sunday)
  oneYearAgo.setDate(oneYearAgo.getDate() - dayOfWeek);

  const current = new Date(oneYearAgo);
  
  // Go until today
  while (current <= today) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const calculateStreaks = (data: Record<string, number>) => {
  // Normalize dates to midnight for accurate day difference calculation
  const getMidnight = (d: Date | string) => {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const activeDates = Object.entries(data)
    .filter(([_, count]) => count > 0)
    .map(([dateStr]) => getMidnight(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());

  if (activeDates.length === 0) return { current: 0, longest: 0 };

  let longest = 0;
  let tempStreak = 0;
  
  // Calculate longest streak
  for (let i = 0; i < activeDates.length; i++) {
    if (i === 0) {
      tempStreak = 1;
    } else {
      const diffTime = activeDates[i].getTime() - activeDates[i - 1].getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        tempStreak++;
      } else {
        tempStreak = 1;
      }
    }
    if (tempStreak > longest) longest = tempStreak;
  }

  // Calculate current streak
  let currentStreak = 0;
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  
  const todayKey = formatDateKey(today);
  const yesterdayKey = formatDateKey(yesterday);
  
  // Start counting from today if active, otherwise start from yesterday if active
  let checkDate: Date | null = null;
  if ((data[todayKey] || 0) > 0) {
    checkDate = today;
  } else if ((data[yesterdayKey] || 0) > 0) {
    checkDate = yesterday;
  }

  if (checkDate) {
    let d = new Date(checkDate);
    // Loop backwards
    // Safety break after 10 years (3650 days) just in case
    for (let i = 0; i < 3650; i++) {
        const key = formatDateKey(d);
        if ((data[key] || 0) > 0) {
            currentStreak++;
            d.setDate(d.getDate() - 1);
        } else {
            break;
        }
    }
  }

  return { current: currentStreak, longest };
};

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ data }) => {
  const dates = useMemo(() => generateYearDates(), []);
  
  const streaks = useMemo(() => calculateStreaks(data), [data]);

  // Determine intensity color based on count
  const getColor = (count: number) => {
    if (count === 0) return 'var(--heatmap-empty)'; // Grey for empty days
    if (count < 3) return '#0e4429';
    if (count < 6) return '#006d32';
    if (count < 10) return '#26a641';
    return '#39d353';
  };
  
  // Group dates by week for column-based rendering
  const weeks = useMemo(() => {
    const weeksArr: Date[][] = [];
    let currentWeek: Date[] = [];
    
    dates.forEach((date) => {
      currentWeek.push(date);
      if (currentWeek.length === 7) {
        weeksArr.push(currentWeek);
        currentWeek = [];
      }
    });
    
    // Push partial week if exists
    if (currentWeek.length > 0) {
      // Pad the last week with nulls or dates to ensure it has 7 days for consistent rendering
      // However, usually partial weeks are at the start or end of the range.
      // If we want to avoid "half columns" being cut off, we ensure full columns are rendered.
      // But if 'data' doesn't exist for future dates, that's fine.
      
      // Actually, generateYearDates() ensures a full range.
      // The issue is likely CSS cutting off a column in half.
      weeksArr.push(currentWeek);
    }
    
    return weeksArr;
  }, [dates]);

  const totalGames = Object.values(data).reduce((acc, curr) => acc + curr, 0);

  const monthLabels = useMemo(() => {
    return weeks.map((week, index) => {
      const firstDay = week[0];
      // Show label if it's the first week, or if the month changed from previous week
      if (index === 0) {
        return firstDay.toLocaleDateString(undefined, { month: 'short' });
      }
      
      const prevWeekFirstDay = weeks[index - 1][0];
      if (firstDay.getMonth() !== prevWeekFirstDay.getMonth()) {
        return firstDay.toLocaleDateString(undefined, { month: 'short' });
      }
      
      return null;
    });
  }, [weeks]);

  return (
    <div className={`category-card profile-card ${styles.heatmapContainer}`}>
      {/* <div className={styles.heatmapHeader}>
        <h2 className="category-card-title">Activity</h2>
      </div> */}
      
      <p style={{marginBottom: '0px', fontSize: '14px', color: '#8b949e'}}>
        {totalGames} games played in the last year
      </p>

      <div className={styles.heatmapContent}>
        <div className={styles.chartContainer}>
          {/* Day Labels (Left) */}
          <div className={styles.dayLabels}>
              {/* 
                 Grid rows: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat 
                 We want labels for Mon, Wed, Fri.
                 Each cell is 10px height + 3px gap. 
                 We can use fixed heights or spacers.
              */}
              {/* Spacer for Sun (index 0) is 10px (height) + 3px (gap) = 13px but flex gap adds 3px */}
              {/* If we assume flex gap is 3px:
                  Sun (0-10px) + Gap (10-13px) -> Total 13px space before Mon.
                  Spacer needs to be 10px. 
                  Spacer (10px) + Gap (3px) = 13px. 
                  Mon starts at 13px. Correct.
              */}
              <div style={{height: '10px'}}></div>
              <div className={styles.dayLabel}>Mon</div>
              {/* Mon (10px) + Gap (3px) + Tue (10px) + Gap (3px) -> Total 26px to Wed 
                  Mon Label is 10px. Gap is 3px.
                  We need to fill Tue (10px) + Gap (3px).
                  So Spacer is 10px.
                  Gap is 3px.
                  Wed starts at 26px?
                  Current pos after Mon label: 13 (start) + 10 (label) = 23.
                  Add gap 3 -> 26px.
                  Start of Tue is 26px.
                  Start of Wed is 39px.
                  We are at 26px.
                  Need to reach 39px.
                  Spacer (10px) + Gap (3px) = 13px.
                  26 + 13 = 39px. Correct.
              */}
              <div style={{height: '10px'}}></div>
               <div className={styles.dayLabel}>Wed</div>
              {/* Similar for Fri */}
              <div style={{height: '10px'}}></div>
               <div className={styles.dayLabel}>Fri</div>
          </div>

          <div className={styles.heatmapGridWrapper}>
              {/* Month Labels (Top) */}
              <div className={styles.monthLabels}>
                  {monthLabels.map((label, i) => (
                      <div key={i} className={styles.monthSpacer}>
                          {label && (
                              <span className={styles.monthLabel}>{label}</span>
                          )}
                      </div>
                  ))}
              </div>

              <div className={styles.heatmapGrid}>
                  {weeks.map((week, wIdx) => (
                  <div key={wIdx} className={styles.heatmapColumn}>
                      {week.map((date) => {
                      const key = formatDateKey(date);
                      const count = data[key] || 0;
                      const dateLabel = date.toLocaleDateString(undefined, {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                      });
                      
                      return (
                          <div
                          key={key}
                          title={`${count} games on ${dateLabel}`}
                          className={styles.heatmapCell}
                          style={{
                              backgroundColor: getColor(count),
                          }}
                          />
                      );
                      })}
                  </div>
                  ))}
              </div>
              <div className={styles.heatmapLegend}>
                  <span>Less</span>
                  <div className={styles.heatmapCell} style={{backgroundColor: 'var(--heatmap-empty)'}}></div>
                  <div className={styles.heatmapCell} style={{backgroundColor: '#0e4429'}}></div>
                  <div className={styles.heatmapCell} style={{backgroundColor: '#006d32'}}></div>
                  <div className={styles.heatmapCell} style={{backgroundColor: '#26a641'}}></div>
                  <div className={styles.heatmapCell} style={{backgroundColor: '#39d353'}}></div>
                  <span>More</span>
              </div>
          </div>
        </div>

        <div className={styles.statsPanel}>
            <div className={styles.statItem}>
                <span className={styles.statValue}>{streaks.longest}</span>
                <span className={styles.statLabel}>Longest Streak</span>
            </div>
            <div className={styles.statItem}>
                <span className={styles.statValue}>{totalGames}</span>
                <span className={styles.statLabel}>Games Played</span>
            </div>
            <div className={styles.statItem}>
                <span className={styles.statValue}>{streaks.current}</span>
                <span className={styles.statLabel}>Current Streak</span>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatmap;
